import { smartQuoteApiConnector } from 'apiConnector/smart-quote';
import { ProductVariationBase } from 'models/productsReferential';
import { useEffect, useReducer } from 'react';
import { httpClient } from 'utils/http/httpClient';
import { extractHttpErrorMessage, HttpClientError } from 'utils/http/httpClient.model';

let cachedProducts: ProductVariationBase[];

type ProductsQueryState = {
  products: ProductVariationBase[];
  status: 'loading' | 'success' | 'error';
  error: Error | null;
};

type ProductsQueryAction =
  | { type: 'fetchStarted' }
  | { type: 'fetchSucceeded'; products: ProductVariationBase[] }
  | { type: 'fetchFailed'; error: Error };

function productsQueryReducer(state: ProductsQueryState, action: ProductsQueryAction): ProductsQueryState {
  switch (action.type) {
    case 'fetchStarted':
      return { ...state, status: 'loading' };
    case 'fetchSucceeded':
      return { products: action.products, status: 'success', error: null };
    case 'fetchFailed':
      return { products: [], status: 'error', error: action.error };
    default:
      return state;
  }
}

export function useSmartQuoteProductsQuery() {
  const [state, dispatch] = useReducer(productsQueryReducer, {
    products: cachedProducts ?? [],
    status: cachedProducts ? 'success' : 'loading',
    error: null,
  });

  useEffect(() => {
    if (cachedProducts) {
      return;
    }

    dispatch({ type: 'fetchStarted' });
    const subscription = smartQuoteApiConnector(httpClient)
      .fetchProducts()
      .subscribe({
        next: data => {
          cachedProducts = data;
          dispatch({ type: 'fetchSucceeded', products: data });
        },
        error: err => {
          dispatch({
            type: 'fetchFailed',
            error: new Error(extractHttpErrorMessage(err as HttpClientError)),
          });
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const isLoading = state.status === 'loading';
  const isError = state.status === 'error';
  const isSuccess = state.status === 'success';

  return { ...state, isLoading, isError, isSuccess };
}
